import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  MdEmail,
  MdPerson,
  MdPhone,
  MdBusiness,
  MdLocationCity,
  MdLanguage,
  MdVpnKey,
} from "react-icons/md";
import { HiOutlineChevronDown } from "react-icons/hi";
import {
  FiCheckCircle,
  FiArrowRight,
  FiArrowLeft,
  FiUsers,
  FiShield,
  FiTool,
} from "react-icons/fi";
import { useAlert } from "../../context/AlertContext";
import AuthCard from "../../components/auth/AuthCard";
import AuthBrand from "../../components/auth/AuthBrand";
import AuthHeaderLink from "../../components/auth/AuthHeaderLink";
import InputField from "../../components/auth/InputField";
import {
  registerApi,
  sendRegistrationOtpApi,
  getLoginTokenApi,
  getAllCountriesApi,
} from "./api";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const ROLES = [
  {
    value: "CLIENT",
    label: "Client",
    Icon: FiUsers,
    accentText: "text-warning",
    accentBg: "bg-warning_bg",
    accentBorder: "border-warning_border",
    description: "Assign projects and manage your construction needs with full progress visibility.",
    features: ["Project Assignment", "Progress Tracking", "Team Management"],
  },
  {
    value: "ADMIN",
    label: "Admin",
    Icon: FiShield,
    accentText: "text-primary",
    accentBg: "bg-primary_tint",
    accentBorder: "border-primary/20",
    description: "Manage projects, employees, and oversee all operations across the platform.",
    features: ["Full System Access", "Employee Management", "Project Oversight"],
  },
  {
    value: "EMPLOYEE",
    label: "Employee",
    Icon: FiTool,
    accentText: "text-success",
    accentBg: "bg-success_bg",
    accentBorder: "border-success_border",
    description: "Work on assigned projects and collaborate seamlessly with your team.",
    features: ["Task Management", "Project Access", "Team Collaboration"],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SelectField
// ─────────────────────────────────────────────────────────────────────────────
function SelectField({ id, label, value, onChange, options, placeholder, error, required }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-[12.5px] font-medium text-text_secondary tracking-wide">
          {label}
          {required && <span className="text-danger ml-0.5"> *</span>}
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={onChange}
          required={required}
          className={`w-full appearance-none rounded border bg-surface_alt px-3.5 py-[11px] text-sm outline-none transition-all duration-150 cursor-pointer
            focus:bg-surface focus:ring-2 focus:ring-primary/[0.12] focus:border-primary/40
            ${value ? "text-text_primary" : "text-text_muted"}
            ${error ? "border-danger/60 bg-danger_bg/40" : "border-border hover:border-border_strong"}`}
        >
          <option value="" disabled>{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <HiOutlineChevronDown
          size={14}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text_muted pointer-events-none"
        />
      </div>
      {error && <p className="text-[11.5px] text-danger">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepIndicator
// ─────────────────────────────────────────────────────────────────────────────
function StepIndicator({ currentStep, completedSteps }) {
  const steps = [
    { num: 1, label: "Select Role" },
    { num: 2, label: "Business" },
    { num: 3, label: "Personal" },
  ];
  return (
    <div className="flex items-start mb-8">
      {steps.map((s, i) => (
        <div key={s.num} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
          <div className="flex flex-col items-center flex-shrink-0">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-colors duration-300 ${
                completedSteps.includes(s.num)
                  ? "bg-success"
                  : s.num === currentStep
                  ? "bg-primary"
                  : "bg-surface_alt"
              }`}
            >
              {completedSteps.includes(s.num) ? (
                <FiCheckCircle size={14} className="text-white" />
              ) : (
                <span
                  className={
                    s.num === currentStep ? "text-white" : "text-text_muted"
                  }
                >
                  {s.num}
                </span>
              )}
            </div>
            <span
              className={`text-[10.5px] mt-1.5 font-medium whitespace-nowrap transition-colors duration-200 ${
                completedSteps.includes(s.num)
                  ? "text-success"
                  : s.num === currentStep
                  ? "text-primary"
                  : "text-text_muted"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className="flex-1 mx-3 mb-5">
              <div
                className={`h-px transition-colors duration-300 ${
                  completedSteps.includes(s.num) ? "bg-success_border" : "bg-border"
                }`}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RoleCard
// ─────────────────────────────────────────────────────────────────────────────
function RoleCard({ role, selected, onSelect }) {
  const { value, label, Icon, accentText, accentBg, accentBorder, description, features } = role;
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(value)}
      className={`relative w-full text-left rounded-lg border-2 p-3.5 transition-all duration-200 flex flex-row sm:flex-col items-start gap-3 sm:gap-2.5 focus:outline-none
        ${
          selected
            ? "border-primary bg-primary/[0.04] shadow-[0_0_0_4px_rgba(53,53,92,0.09)]"
            : "border-border bg-surface"
        }`}
    >
      {/* Selected badge */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="badge"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center"
          >
            <FiCheckCircle size={10} className="text-white" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Icon */}
      <div
        className={`w-10 h-10 sm:w-9 sm:h-9 rounded-xl ${accentBg} border ${accentBorder} flex items-center justify-center flex-shrink-0`}
      >
        <Icon size={18} className={accentText} />
      </div>

      {/* Content — fills row on mobile, stacks below icon on sm+ */}
      <div className="flex-1 min-w-0 pr-5 sm:pr-0">
        <p className="text-[13px] font-bold text-text_primary leading-none">{label}</p>
        <p className="text-[11px] text-text_secondary mt-1.5 leading-relaxed">{description}</p>

        {/* Feature bullets */}
        <ul className="flex flex-col gap-1 mt-2">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-1.5 text-[10.5px] text-text_secondary">
              <div
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-200 ${
                  selected ? "bg-primary" : "bg-border_strong"
                }`}
              />
              {f}
            </li>
          ))}
        </ul>
      </div>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SuccessView
// ─────────────────────────────────────────────────────────────────────────────
function SuccessView({ email }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center gap-5 py-6 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.1 }}
        className="w-14 h-14 rounded-2xl bg-success_bg border border-success_border flex items-center justify-center"
      >
        <FiCheckCircle size={26} className="text-success" />
      </motion.div>
      <div>
        <h2 className="text-xl font-bold text-text_primary tracking-tight">Registration submitted</h2>
        <p className="text-sm text-text_secondary mt-2 leading-relaxed max-w-[320px] mx-auto">
          Your account is pending admin approval. Once approved, your login credentials will be
          sent to{" "}
          <span className="text-text_primary font-medium">{email}</span>.
        </p>
      </div>
      <Link
        to="/login"
        className="px-5 py-2.5 rounded bg-primary text-white text-[13.5px] font-semibold"
      >
        Back to Sign In
      </Link>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Register
// ─────────────────────────────────────────────────────────────────────────────
export default function Register() {
  const { showAlert } = useAlert();

  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpDisabled, setOtpDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    email: "",
    company_name: "",
    company_url: "",
    first_name: "",
    last_name: "",
    role: "",
    latitude: "",
    longitude: "",
    city: "",
    country_directory_fid: "",
    phone: "",
    pin: "",
    refrence_code: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      setDataLoading(true);
      try {
        const cRes = await getAllCountriesApi();
        if (cRes.data?.status) setCountries(cRes.data.data || []);
      } catch {
        // silent — user can still fill other fields
      } finally {
        setDataLoading(false);
      }
    })();

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) =>
          setForm((p) => ({
            ...p,
            latitude: coords.latitude.toString(),
            longitude: coords.longitude.toString(),
          })),
        () => {}
      );
    }
  }, []);

  const handleField = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validateStep = (n) => {
    const errs = {};
    if (n === 1) {
      if (!form.role) errs.role = "Please select a role.";
    }
    if (n === 2) {
      if (!form.country_directory_fid) errs.country_directory_fid = "Country is required.";
      if (!form.city.trim()) errs.city = "City is required.";
      if ((form.role === "ADMIN" || form.role === "CLIENT") && !form.company_name.trim())
        errs.company_name = "Company name is required.";
    }
    if (n === 3) {
      if (!form.first_name.trim()) errs.first_name = "First name is required.";
      if (!form.email) errs.email = "Email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        errs.email = "Enter a valid work email address.";
      if (otpSent && !otp) errs.otp = "OTP is required.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    if (step === 1 && form.role === "ADMIN") {
      setForm((p) => ({ ...p, company_name: "Surya Group" }));
    }
    setCompletedSteps((p) => [...new Set([...p, step])]);
    setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  const handleSendOtp = async () => {
    if (!form.email) {
      showAlert({ type: "warning", message: "Please enter an email address first." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      showAlert({ type: "warning", message: "Please enter a valid work email address." });
      return;
    }
    setOtpDisabled(true);
    try {
      const res = await sendRegistrationOtpApi({ email: form.email });
      if (res.data?.status) {
        setOtpSent(true);
        showAlert({ type: "success", message: res.data.message });
        setCountdown(30);
        const timer = setInterval(() => {
          setCountdown((c) => {
            if (c <= 1) {
              clearInterval(timer);
              setOtpDisabled(false);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      } else {
        showAlert({ type: "error", message: res.data?.message || "Failed to send OTP." });
        setOtpDisabled(false);
      }
    } catch {
      showAlert({ type: "error", message: "Failed to send OTP. Please try again." });
      setOtpDisabled(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    if (!otpSent || !otp) {
      showAlert({ type: "error", message: "Please verify your email with OTP first." });
      return;
    }
    setIsCreatingAccount(true);
    try {
      const otpRes = await getLoginTokenApi({ email: form.email, otp });
      if (!otpRes.data?.status) {
        showAlert({ type: "error", message: otpRes.data?.message || "Invalid OTP." });
        return;
      }
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      const res = await registerApi(payload);
      if (res.data?.status) {
        showAlert({ type: "success", message: res.data.message });
        setCompletedSteps((p) => [...new Set([...p, 3])]);
        setSuccess(true);
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        showAlert({ type: "error", message: res.data?.message || "Registration failed." });
      }
    } catch (err) {
      showAlert({
        type: "error",
        message: err?.response?.data?.message || "Registration failed. Please try again.",
      });
    } finally {
      setIsCreatingAccount(false);
    }
  };

  return (
    <AuthCard
      maxWidth="max-w-[480px]"
      headerRight={
        !success && <AuthHeaderLink prompt="Already have an account?" to="/login" label="Sign in" />
      }
    >
      <AnimatePresence mode="wait">
        {success ? (
          <SuccessView key="success" email={form.email} />
        ) : (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Brand — only on small screens, where the brand panel is hidden */}
            <div className="lg:hidden">
              <AuthBrand />
            </div>

            {/* Heading */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
                Create your account
              </h1>
              <p className="text-[13.5px] text-text_muted mt-1.5">
                Join SwiftBoard in three quick steps.
              </p>
            </div>

            {/* Step indicator */}
            <StepIndicator currentStep={step} completedSteps={completedSteps} />

            {/* Step content */}
            <AnimatePresence mode="wait">

              {/* ── Step 1 — Role selection ── */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -28 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  <div className="mb-4">
                    <p className="text-[13px] font-semibold text-text_primary">Select your role</p>
                    <p className="text-[11.5px] text-text_muted mt-0.5">
                      Choose how you'll use SwiftBoard. Accurate company details are required for approval.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {ROLES.map((role) => (
                      <RoleCard
                        key={role.value}
                        role={role}
                        selected={form.role === role.value}
                        onSelect={(v) => {
                          setForm((p) => ({ ...p, role: v }));
                          if (errors.role) setErrors((p) => ({ ...p, role: "" }));
                        }}
                      />
                    ))}
                  </div>

                  {errors.role && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-[11.5px] text-danger mt-2"
                    >
                      {errors.role}
                    </motion.p>
                  )}
                </motion.div>
              )}

              {/* ── Step 2 — Business details ── */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -28 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="flex flex-col gap-3.5"
                >
                  <div className="mb-0.5">
                    <p className="text-[13px] font-semibold text-text_primary">Business details</p>
                    <p className="text-[11.5px] text-text_muted mt-0.5">
                      Accurate company information ensures seamless account approval.
                    </p>
                  </div>

                  {(form.role === "ADMIN" || form.role === "CLIENT") && (
                    <InputField
                      id="company_name"
                      label="Company name"
                      value={form.company_name}
                      onChange={handleField("company_name")}
                      placeholder={form.role === "ADMIN" ? "Surya Group" : "Your company"}
                      icon={MdBusiness}
                      error={errors.company_name}
                      required
                      readOnly={form.role === "ADMIN"}
                      autoComplete="organization"
                    />
                  )}

                  {(form.role === "ADMIN" || form.role === "CLIENT") && (
                    <InputField
                      id="company_url"
                      label="Company website (optional)"
                      value={form.company_url}
                      onChange={handleField("company_url")}
                      placeholder="https://www.example.com"
                      icon={MdLanguage}
                    />
                  )}

                  {/* Trade of work is hidden — admin assigns trades manually after approval. */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SelectField
                      id="country_directory_fid"
                      label="Country"
                      value={form.country_directory_fid}
                      onChange={handleField("country_directory_fid")}
                      options={countries.map((c) => ({
                        value: String(c.id),
                        label: c.country_name,
                      }))}
                      placeholder={dataLoading ? "Loading..." : "Select country"}
                      error={errors.country_directory_fid}
                      required
                    />
                    <InputField
                      id="city"
                      label="City"
                      value={form.city}
                      onChange={handleField("city")}
                      placeholder="Mumbai"
                      icon={MdLocationCity}
                      error={errors.city}
                      required
                      autoComplete="address-level2"
                    />
                  </div>

                  {form.role === "EMPLOYEE" && (
                    <InputField
                      id="refrence_code"
                      label="Reference code (optional)"
                      value={form.refrence_code}
                      onChange={handleField("refrence_code")}
                      placeholder="Enter reference code"
                      icon={MdVpnKey}
                    />
                  )}
                </motion.div>
              )}

              {/* ── Step 3 — Personal info ── */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -28 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="flex flex-col gap-3.5"
                >
                  <div className="mb-0.5">
                    <p className="text-[13px] font-semibold text-text_primary">Personal information</p>
                    <p className="text-[11.5px] text-text_muted mt-0.5">
                      Enter your details and verify your email address with a one-time code.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InputField
                      id="first_name"
                      label="First name"
                      value={form.first_name}
                      onChange={handleField("first_name")}
                      placeholder="John"
                      icon={MdPerson}
                      error={errors.first_name}
                      required
                      autoComplete="given-name"
                    />
                    <InputField
                      id="last_name"
                      label="Last name (optional)"
                      value={form.last_name}
                      onChange={handleField("last_name")}
                      placeholder="Doe"
                      autoComplete="family-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InputField
                      id="phone"
                      label="Phone (optional)"
                      type="tel"
                      value={form.phone}
                      onChange={handleField("phone")}
                      placeholder="+91 98765 43210"
                      icon={MdPhone}
                      autoComplete="tel"
                    />
                    <InputField
                      id="pin"
                      label="PIN (optional)"
                      value={form.pin}
                      onChange={handleField("pin")}
                      placeholder="400001"
                      icon={MdVpnKey}
                    />
                  </div>

                  {/* Email + OTP button */}
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="email"
                      className="text-[12.5px] font-medium text-text_secondary tracking-wide"
                    >
                      Work Email <span className="text-danger">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1 min-w-0">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text_muted pointer-events-none">
                          <MdEmail size={15} />
                        </div>
                        <input
                          id="email"
                          type="email"
                          value={form.email}
                          onChange={handleField("email")}
                          placeholder="Enter your work email address"
                          autoComplete="email"
                          className={`w-full rounded border bg-surface_alt py-[11px] pl-[2.375rem] pr-4 text-sm text-text_primary placeholder:text-text_muted outline-none transition-all duration-150
                            focus:bg-surface focus:ring-2 focus:ring-primary/[0.12] focus:border-primary/40
                            ${
                              errors.email
                                ? "border-danger/60 bg-danger_bg/40"
                                : "border-border hover:border-border_strong"
                            }`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpDisabled || !form.email}
                        className="shrink-0 rounded border border-primary/25 bg-primary/[0.06] px-3.5 py-[11px] text-[12px] font-semibold text-primary disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {countdown > 0 ? `${countdown}s` : otpSent ? "Resend" : "Get OTP"}
                      </button>
                    </div>
                    {errors.email && (
                      <p className="text-[11.5px] text-danger">{errors.email}</p>
                    )}
                  </div>

                  {/* OTP input — slides in after OTP is sent */}
                  <AnimatePresence>
                    {otpSent && (
                      <motion.div
                        key="otp-field"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-1.5 pt-0.5">
                          <label
                            htmlFor="otp"
                            className="text-[12.5px] font-medium text-text_secondary tracking-wide"
                          >
                            Verification OTP <span className="text-danger">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text_muted pointer-events-none">
                              <MdVpnKey size={15} />
                            </div>
                            <input
                              id="otp"
                              type="text"
                              value={otp}
                              onChange={(e) => {
                                setOtp(e.target.value);
                                if (errors.otp) setErrors((p) => ({ ...p, otp: "" }));
                              }}
                              placeholder="Enter 6-digit OTP"
                              maxLength={6}
                              autoComplete="one-time-code"
                              className={`w-full rounded border bg-surface_alt py-[11px] pl-[2.375rem] pr-4 text-sm text-text_primary placeholder:text-text_muted outline-none transition-all duration-150
                                focus:bg-surface focus:ring-2 focus:ring-primary/[0.12] focus:border-primary/40
                                ${
                                  errors.otp
                                    ? "border-danger/60 bg-danger_bg/40"
                                    : "border-border hover:border-border_strong"
                                }`}
                            />
                          </div>
                          {errors.otp && (
                            <p className="text-[11.5px] text-danger">{errors.otp}</p>
                          )}
                          <p className="text-[11px] text-text_muted">
                            Check your inbox — and spam folder if you don't see it.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div
              className={`flex gap-3 mt-6 ${step > 1 ? "justify-between" : "justify-end"}`}
            >
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 px-4 py-2.5 rounded border border-border bg-surface text-[13.5px] font-medium text-text_secondary"
                >
                  <FiArrowLeft size={14} />
                  Back
                </button>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 bg-primary text-white text-[13.5px] font-semibold px-5 py-2.5 rounded"
                >
                  Continue
                  <FiArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isCreatingAccount}
                  className="flex items-center gap-2 bg-primary text-white text-[13.5px] font-semibold px-5 py-2.5 rounded disabled:opacity-55 disabled:cursor-not-allowed"
                >
                  {isCreatingAccount ? (
                    <div className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <FiCheckCircle size={14} />
                      Create Account
                    </>
                  )}
                </button>
              )}
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </AuthCard>
  );
}
